import $ from 'jquery';

import { getCountryInfoUrl } from 'chairisher/context/location';

/**
 * Handles updating form elements when the country changes.
 * For example, this will change a state dropdown to provinces if Canada is selected.
 */
class CountryView {
    /**
     * @param {Object} options
     * @param {boolean=} options.shouldLimitToShippableStates
     * @param {jQuery} options.$countryEl
     */
    constructor({ shouldLimitToShippableStates = false, $countryEl } = {}) {
        /**
         * The actual dropdown menu that toggles country.
         *
         * @type {jQuery}
         */
        this.$countryEl = $countryEl;

        /**
         * The selected fieldset, if any.
         *
         * @type {jQuery}
         */
        this.$selectedFieldset = null;

        /**
         * The selected form, if any.
         *
         * @type {jQuery}
         */
        this.$selectedForm = null;

        /**
         * Selects the input element for a user's postal code.
         *
         * @type {string}
         */
        this.postalCodeSelector = '.js-address-zip';

        /**
         * Whether the state dropdown should contain only shippable options.
         *
         * @type {boolean}
         */
        this.shouldLimitToShippableStates = shouldLimitToShippableStates;

        /**
         * Selects the dropdown menu or input element that changes a user's state selection.
         *
         * @type {string}
         */
        this.stateSelector = '.js-address-state';
    }

    bind() {
        this.$countryEl.on('change', 'select', (event) => {
            event.preventDefault();

            const $target = $(event.target);

            this.updateFromSelection($target);
            this.disableFields(true);
            this.submitData(this.getCountryCode());
        });
    }

    /**
     * Returns the current country code that is set.
     *
     * @returns {string|undefined}
     */
    getCountryCode() {
        return this.$countryEl.find('select').val();
    }

    /**
     * @returns {jQuery}
     */
    getPostalCodeEl() {
        return this.$selectedFieldset.find(this.postalCodeSelector).find('input');
    }

    /**
     * @returns {jQuery}
     */
    getStateEl() {
        return this.$selectedFieldset.find(this.stateSelector).find('input, select');
    }

    /**
     * Switches the $stateEl from input to select (or visa-versa) when the country is toggled from generic to primary.
     *
     * @param {boolean} shouldBeInput Indicates if the element should become an input or select.
     * @returns {jQuery} The toggled $stateEl
     */
    toggleStateEl(shouldBeInput) {
        const $stateEl = this.getStateEl();

        let properties = {
            class: $stateEl.attr('class'),
            id: $stateEl.attr('id'),
            name: $stateEl.attr('name'),
        };

        if (shouldBeInput) {
            properties = {
                autocomplete: 'state',
                placeholder: 'State',
                type: 'text',
                ...properties,
            };
        }

        $stateEl.replaceWith($(shouldBeInput ? '<input/>' : '<select></select>', properties));

        return this.getStateEl();
    }

    /**
     * Called when a user changes country to bind elements to the appropriate fieldset.
     *
     * @param {jQuery} $target The jQuery-ized DOM element which triggered the update.
     */
    updateFromSelection($target) {
        this.$selectedFieldset = $target.closest('fieldset');
        this.$selectedForm = $target.closest('form');

        // Hide the country <select> when there is only one option.
        this.$countryEl.toggleClass('hidden', this.$countryEl.find('option').length <= 1);
    }

    /**
     * Set the state label.
     *
     * @param text {string} The text for the label.
     */
    setStateLabel(text) {
        this.$selectedFieldset.find(this.stateSelector).find('label').html(text);
    }

    /**
     * Sets whether the state shipping options should be limited to shippable only.
     *
     * @param {boolean} shouldBeShippable
     */
    setShouldLimitToShippableStates(shouldBeShippable) {
        this.shouldLimitToShippableStates = shouldBeShippable;
    }

    /**
     * Enables or disables fields for safer AJAX requests.
     *
     * @param {boolean} shouldDisable Indicates whether the elements should be enabled or disabled.
     */
    disableFields(shouldDisable) {
        this.$selectedForm.find('input, select').prop('disabled', shouldDisable);
    }

    /**
     * Sends an AJAX request, updates fields with the resulting data, and then re-enables them.
     *
     * @param {string} countryCode The requested country code.
     */
    submitData(countryCode) {
        return $.ajax({
            data: {
                country_code: countryCode,
                should_limit_to_shippable_states: this.shouldLimitToShippableStates,
            },
            method: 'GET',
            url: getCountryInfoUrl(),
        })
            .done(
                ({
                    postal_code_inputmode: postalCodeInputmode,
                    postal_code_regex: postalCodeRegex,
                    state_choices: stateChoices,
                    state_label: stateLabel,
                }) => {
                    let $stateEl = this.getStateEl();
                    const isSelect = $stateEl.is('select');

                    if (stateChoices.length) {
                        // This is a primary country.
                        if (!isSelect) {
                            $stateEl = this.toggleStateEl(isSelect);
                        }

                        $stateEl.html(
                            stateChoices.map(([value, text]) =>
                                $('<option></option>', {
                                    text,
                                    value,
                                }),
                            ),
                        );
                    } else if (isSelect) {
                        // Toggling from primary to generic country: change field type.
                        this.toggleStateEl(isSelect);
                    }

                    this.setStateLabel(stateLabel);
                    this.getPostalCodeEl().attr('inputmode', postalCodeInputmode);
                    this.getPostalCodeEl().attr('pattern', postalCodeRegex);
                },
            )
            .always(() => this.disableFields(false)); // Enable fields
    }

    /**
     * Loads the country data for the current selection.
     *
     * @returns {$.Deferred}
     */
    updateForCurrentCountry() {
        this.updateFromSelection(this.$countryEl);
        const currentState = this.getStateEl().val();
        return this.submitData(this.getCountryCode()).done(() => {
            // Restore the previously selected state because the html element was recreated.
            this.getStateEl().val(currentState);
        });
    }

    /**
     * Updates country data for a new country.
     *
     * @param {string} countryCode The new country code
     * @param {jQuery} $target The jQuery element that triggered the update
     * @returns {$.Deferred}
     */
    updateForCountry(countryCode, $target) {
        this.updateFromSelection($target);
        return this.submitData(countryCode);
    }
}

export default CountryView;
